/**
 * Bundle Registry - Central point for bundle configuration
 * Maps routes to their corresponding bundles and defines roles needed
 */

import { PUBLIC_ROUTES, PUBLIC_BUNDLE_PATH } from './public.config';
import { USER_ROUTES, USER_BUNDLE_PATH } from './user.config';
import { CUSTOMER_ADMIN_ROUTES, CUSTOMER_ADMIN_BUNDLE_PATH } from './customer-admin.config';
import { SYSTEM_ADMIN_ROUTES, SYSTEM_ADMIN_BUNDLE_PATH } from './system-admin.config';

export type BundleType = 'public' | 'user' | 'customer-admin' | 'system-admin';

export interface BundleConfig {
    name: BundleType;
    routes: readonly string[];
    path: string;
    requiredRoles: string[] | null;
}

export const BUNDLE_REGISTRY: Record<BundleType, BundleConfig> = {
    'public': {
        name: 'public',
        routes: PUBLIC_ROUTES,
        path: PUBLIC_BUNDLE_PATH,
        requiredRoles: null // No authentication required
    },
    'user': {
        name: 'user',
        routes: USER_ROUTES,
        path: USER_BUNDLE_PATH,
        // Every authenticated, non-service role needs the user bundle —
        // home, content, reports, etc. The original list omitted
        // TENANT_ADMIN (and the RESOURCE_* / VIEWER tiers), so a
        // CUSTOMER_ADMIN impersonating into a workspace (effective role
        // TENANT_ADMIN) couldn't even load /home.
        // METALARK-929: PARTNER_ADMIN is an authenticated non-service role and
        // needs the base user bundle to land after login.
        requiredRoles: ['TENANT_USER', 'TENANT_ADMIN', 'TENANT_VIEWER', 'RESOURCE_MANAGER', 'RESOURCE_CONTRIBUTOR', 'CUSTOMER_ADMIN', 'PARTNER_ADMIN', 'SYSTEM_ADMIN']
    },
    'customer-admin': {
        name: 'customer-admin',
        routes: CUSTOMER_ADMIN_ROUTES,
        path: CUSTOMER_ADMIN_BUNDLE_PATH,
        // METALARK-929: PARTNER_ADMIN shares the customer-admin bundle for the
        // partner-scoped Customer 360 + webhook subscriptions; pages self-scope
        // to the partner and the backend enforces partner ownership. (An entered
        // partner runs as effective CUSTOMER_ADMIN and uses this bundle anyway.)
        requiredRoles: ['CUSTOMER_ADMIN', 'PARTNER_ADMIN', 'SYSTEM_ADMIN']
    },
    'system-admin': {
        name: 'system-admin',
        routes: SYSTEM_ADMIN_ROUTES,
        path: SYSTEM_ADMIN_BUNDLE_PATH,
        requiredRoles: ['SYSTEM_ADMIN']
    }
};

/**
 * Get the bundle type for a given route name
 * @param routeName - The route name to look up
 * @returns The bundle type or null if not found
 */
export function getBundleForRoute(routeName: string): BundleType | null {
    for (const [bundleType, config] of Object.entries(BUNDLE_REGISTRY)) {
        if (config.routes.includes(routeName as any)) {
            return bundleType as BundleType;
        }
    }
    return null;
}

/**
 * Get the path to a bundle's chunk file
 * @param bundleType - The bundle type
 * @returns The path pattern for the bundle
 */
export function getBundlePath(bundleType: BundleType): string {
    return BUNDLE_REGISTRY[bundleType].path;
}

/**
 * Check if a role is allowed to access a bundle
 * @param bundleType - The bundle type
 * @param userRole - The user's role
 * @returns true if the role can access the bundle
 */
export function canAccessBundle(bundleType: BundleType, userRole: string | null): boolean {
    const config = BUNDLE_REGISTRY[bundleType];
    
    // Public bundle is always accessible
    if (!config.requiredRoles) {
        return true;
    }
    
    // Check if user's role is in required roles
    if (!userRole) {
        return false;
    }
    
    return config.requiredRoles.includes(userRole);
}

/**
 * Get all bundles a user can access based on their role
 * @param userRole - The user's role
 * @returns Array of bundle types the user can access
 */
export function getAccessibleBundles(userRole: string | null): BundleType[] {
    const accessible: BundleType[] = [];
    
    // Public bundle is always accessible
    accessible.push('public');
    
    if (!userRole) {
        return accessible;
    }
    
    // Add bundles based on role
    Object.entries(BUNDLE_REGISTRY).forEach(([bundleType, config]) => {
        if (bundleType !== 'public' && config.requiredRoles?.includes(userRole)) {
            accessible.push(bundleType as BundleType);
        }
    });
    
    return accessible;
}

/**
 * Get bundle hierarchy for preloading
 * Some bundles depend on others being loaded first
 * @param bundleType - The bundle type
 * @returns Array of bundle types that must be loaded before this one
 */
export function getBundleDependencies(bundleType: BundleType): BundleType[] {
    switch (bundleType) {
        case 'customer-admin':
        case 'system-admin':
            // Admin bundles depend on user bundle
            return ['user'];
        case 'user':
            // User bundle has no dependencies
            return [];
        case 'public':
            // Public bundle is always available
            return [];
        default:
            return [];
    }
}
